"use client";

import { ReactNode } from "react";
import { SUPPORTED_EXCHANGES } from "@cede/types";
import { DISCORD_INVITE_URL, getCexLabel } from "@cede/utils";
import { InformationModal } from "./InformationModal";
import { Button } from "@/components/ui/button";
import { useOAuthSupportMap } from "@/hooks/exchangeAuthentication/useOAuthSupportMap";
import { useCreateAccountStore } from "@/store/accountStore";
import { FLOW_TYPES, STEPS, useConnectStore } from "@/store/connectStore";
import { useSubAccountRemovedStore } from "@/store/useSubAccountRemovedStore";
import { enqueueSnackbar } from "notistack";

const Bold = ({ children }: { children: ReactNode }) => {
  return <span className="font-semibold text-cede-text-primary">{children}</span>;
};

type SubAccountRemovedModalContentProps = {
  onConnect: () => void;
  exchangeIds: SUPPORTED_EXCHANGES[];
};

const SubAccountRemovedModalContent = ({ exchangeIds, onConnect }: SubAccountRemovedModalContentProps) => {
  return (
    <div className="space-y-6 text-base text-cede-text-secondary">
      <div className="space-y-3 leading-relaxed">
        <p>
          <Bold>Important Update:</Bold> Cede Hub no longer supports sub-accounts.
        </p>
        <p>
          All {exchangeIds.map((exchange) => getCexLabel(exchange)).join(", ")} sub-accounts linked to your profile have
          been automatically removed to ensure platform security and compliance.
        </p>
        <p>
          To continue trading on campaigns, please connect your <Bold>main account</Bold> instead.
        </p>
        <p className="text-sm text-cede-text-tertiary">
          Note: Your trading history and positions from sub-accounts remain preserved in your profile statistics.
        </p>
      </div>
      <div className="flex flex-col items-center gap-3 pt-2">
        <Button
          variant="primary"
          size="large"
          className="h-12 w-full rounded-2xl text-base font-semibold sm:w-[65%]"
          onClick={onConnect}
        >
          Connect Main Account
        </Button>
        <a
          href={DISCORD_INVITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-center text-sm font-medium text-cede-primary underline"
        >
          Need help? Contact our support team
        </a>
      </div>
    </div>
  );
};

export const SubAccountRemovedModal = () => {
  const { open, setOpen, exchangeIds } = useSubAccountRemovedStore();
  const { setOpenConnectModal, setExchangeIds, setFlowType, setActiveStep } = useConnectStore();
  const { updateFormData } = useCreateAccountStore();
  const oAuthSupportMap = useOAuthSupportMap();

  const handleConnect = () => {
    const firstExchangeId = exchangeIds[0];
    if (!firstExchangeId) {
      enqueueSnackbar("No exchanges available to reconnect", { variant: "error" });
      return;
    }

    setExchangeIds([firstExchangeId]);
    setFlowType(FLOW_TYPES.RELINK);
    const step = oAuthSupportMap[firstExchangeId] ? STEPS.LinkAccount : STEPS.ApiKeys;
    setActiveStep(step);
    setOpenConnectModal(true);
    updateFormData({ selectedCex: firstExchangeId });
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <InformationModal open={open} onClose={handleClose}>
      <SubAccountRemovedModalContent exchangeIds={exchangeIds} onConnect={handleConnect} />
    </InformationModal>
  );
};
